/**
 * English: Merge device agents from /api/agents with /api/devices/list rows by device_id.
 * Thai: รวม device agents จาก /api/agents กับแถว /api/devices/list ตาม device_id
 */
import { useMemo, useCallback } from 'react';
import { useAgents } from '../../hooks/useAgents';
import { useDeviceList } from '../../hooks/useDeviceList';
import { useStation } from '../../context/StationContext';
import type { Agent, DeviceInfo } from '../../types';

export interface AgentRow {
  agent: Agent;
  device: DeviceInfo | null;
  deviceId: string;
}

/**
 * Strip `device_{station_id}_` prefix from agent_id to recover device_id.
 */
export function extractDeviceIdFromAgentId(agentId: string, stationId: string): string {
  const prefix = `device_${stationId}_`;
  if (agentId.startsWith(prefix)) {
    return agentId.slice(prefix.length);
  }
  return agentId;
}

export interface UseAgentRowsResult {
  rows: AgentRow[];
  loading: boolean;
  awaitingStation: boolean;
  refresh: () => Promise<void>;
  activeStationId: string;
  /** Distinct device_type values from /api/devices/list (for filter dropdown). */
  deviceTypes: string[];
}

export function useAgentRows(stationIdOverride?: string): UseAgentRowsResult {
  const { activeStationId: contextStationId } = useStation();
  const effectiveStationId = (stationIdOverride?.trim() || contextStationId).trim();
  const { deviceAgents, loading: agentsLoading, refresh: refreshAgents } = useAgents(effectiveStationId);
  const {
    devices,
    deviceTypes,
    loading: devicesLoading,
    awaitingStation,
    refresh: refreshDeviceList,
  } = useDeviceList(effectiveStationId);

  const deviceById = useMemo(() => {
    const m = new Map<string, DeviceInfo>();
    for (const d of devices) {
      m.set(d.device_id, d);
    }
    return m;
  }, [devices]);

  const rows: AgentRow[] = useMemo(() => {
    if (!effectiveStationId) return [];
    return deviceAgents.map((agent) => {
      const deviceId = extractDeviceIdFromAgentId(agent.id, effectiveStationId).toLowerCase();
      return {
        agent,
        device: deviceById.get(deviceId) ?? null,
        deviceId,
      };
    });
  }, [deviceAgents, deviceById, effectiveStationId]);

  const refresh = useCallback(async () => {
    await Promise.all([refreshAgents(), refreshDeviceList()]);
  }, [refreshAgents, refreshDeviceList]);

  return {
    rows,
    loading: agentsLoading || devicesLoading,
    awaitingStation,
    refresh,
    activeStationId: effectiveStationId,
    deviceTypes,
  };
}
